import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FolderTree, Plus, Search, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CompactTableDate, commerceTableClassName } from '@/components/common/compact-table-date'
import { getErrorMessage } from '@/lib/api'
import { cn, formatCurrency } from '@/lib/utils'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { categoriesApi } from '@/services/categories'
import { jobsApi } from '@/services/jobs'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All status' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All scope' },
  { value: 'open', label: 'Open scope' },
  { value: 'completed', label: 'Completed scope' },
] as const

const statusBadgeClassName = (status?: string) => {
  switch (status) {
    case 'completed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'in_progress':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'on_hold':
      return 'border-orange-200 bg-orange-50 text-orange-700'
    case 'cancelled':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'draft':
      return 'border-slate-200 bg-slate-50 text-slate-700'
    default:
      return 'border-blue-200 bg-blue-50 text-blue-700'
  }
}

const priorityBadgeClassName = (priority?: string) => {
  switch (priority) {
    case 'critical':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'urgent':
      return 'border-orange-200 bg-orange-50 text-orange-700'
    case 'high':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700'
  }
}

const normalizeStatusLabel = (value?: string) => {
  if (!value) {
    return 'Open'
  }
  return value
    .split('_')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')
}

export default function ProjectsListPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]['value']>('all')
  const [scopeFilter, setScopeFilter] = useState<(typeof SCOPE_OPTIONS)[number]['value']>('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [page, setPage] = useState(1)

  const projectsQuery = useQuery({
    queryKey: ['projects-dashboard', page, search, statusFilter, scopeFilter, categoryFilter, serviceInstanceId],
    queryFn: () => jobsApi.getProjects({
      page,
      page_size: 20,
      search: search.trim() || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      job_scope: scopeFilter === 'all' ? undefined : scopeFilter,
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      ordering: '-updated_at',
      service_instance_id: serviceInstanceId || undefined,
    }),
  })

  const categoriesQuery = useQuery({
    queryKey: ['projects-dashboard-categories', serviceInstanceId],
    queryFn: () => categoriesApi.getCategories({ kind: 'project' }),
    staleTime: 60_000,
  })

  const activeProjectsCountQuery = useQuery({
    queryKey: ['projects-dashboard-kpi-active', serviceInstanceId],
    queryFn: () => jobsApi.getProjects({ page: 1, page_size: 1, job_scope: 'open', service_instance_id: serviceInstanceId || undefined }),
    staleTime: 60_000,
  })

  const completedProjectsCountQuery = useQuery({
    queryKey: ['projects-dashboard-kpi-completed', serviceInstanceId],
    queryFn: () => jobsApi.getProjects({ page: 1, page_size: 1, job_scope: 'completed', service_instance_id: serviceInstanceId || undefined }),
    staleTime: 60_000,
  })

  const projects = projectsQuery.data?.results ?? []
  const totalCount = projectsQuery.data?.count ?? 0
  const activeCount = activeProjectsCountQuery.data?.count ?? 0
  const completedCount = completedProjectsCountQuery.data?.count ?? 0

  const overdueCountOnPage = useMemo(() => {
    const now = Date.now()
    return projects.filter((project) => {
      if (!project?.scheduled_end) {
        return false
      }
      if (project.status === 'completed' || project.status === 'cancelled') {
        return false
      }
      const endMs = new Date(project.scheduled_end).getTime()
      return Number.isFinite(endMs) && endMs < now
    }).length
  }, [projects])

  const totalPages = Math.max(1, Math.ceil(totalCount / 20))

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setScopeFilter('all')
    setCategoryFilter('all')
    setPage(1)
  }

  const hasActiveFilters = Boolean(search.trim()) || statusFilter !== 'all' || scopeFilter !== 'all' || categoryFilter !== 'all'

  return (
    <div className="space-y-4 pb-2">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Badge variant="outline" className="w-fit rounded-md border-blue-200 bg-blue-50 text-blue-700">
            Project workspace
          </Badge>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Projects</h1>
            <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
              <FolderTree className="mr-1 inline h-3.5 w-3.5" />
              {totalCount} total
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Project dashboard and table are managed independently from job operations.
          </p>
        </div>

        <Button size="sm" className="rounded-lg" asChild>
          <Link to={withServiceInstanceId('/projects/create', serviceInstanceId)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Project
          </Link>
        </Button>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {projectsQuery.isLoading ? (
          Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <Card className="rounded-xl border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total projects</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-semibold">{totalCount}</p></CardContent>
            </Card>
            <Card className="rounded-xl border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Active projects</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-semibold">{activeCount}</p></CardContent>
            </Card>
            <Card className="rounded-xl border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Completed projects</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-semibold">{completedCount}</p></CardContent>
            </Card>
            <Card className="rounded-xl border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Overdue on page</CardTitle></CardHeader>
              <CardContent><p className="text-2xl font-semibold">{overdueCountOnPage}</p></CardContent>
            </Card>
          </>
        )}
      </section>

      <Card className="overflow-hidden rounded-[24px] border-white/70 bg-card/90 shadow-[0_20px_40px_-35px_rgba(15,23,42,0.55)]">
        <CardHeader className="space-y-3 border-b border-border/60 pb-3">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-base">Project table</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search project, customer, or reference"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value)
                    setPage(1)
                  }}
                  className="h-9 pl-9"
                />
              </div>

              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value as (typeof STATUS_OPTIONS)[number]['value'])
                setPage(1)
              }}>
                <SelectTrigger className="h-9 w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={scopeFilter} onValueChange={(value) => {
                setScopeFilter(value as (typeof SCOPE_OPTIONS)[number]['value'])
                setPage(1)
              }}>
                <SelectTrigger className="h-9 w-[160px]">
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={(value) => {
                setCategoryFilter(value)
                setPage(1)
              }}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {(categoriesQuery.data?.results ?? []).map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters ? (
                <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {projectsQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="rounded-lg border border-border/70 p-3">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="mt-2 h-3.5 w-full" />
                </div>
              ))}
            </div>
          ) : projectsQuery.error ? (
            <div className="p-5 text-sm text-destructive">{getErrorMessage(projectsQuery.error)}</div>
          ) : projects.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No projects matched the selected filters.
            </div>
          ) : (
            <div className={commerceTableClassName}>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Project</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Timeline</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{project.title || `Project #${project.id}`}</p>
                          {project.job_ref ? <p className="text-xs text-muted-foreground">{project.job_ref}</p> : null}
                        </div>
                      </TableCell>
                      <TableCell>{project.category_name || '-'}</TableCell>
                      <TableCell>{project.client_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('capitalize', statusBadgeClassName(project.status))}>
                          {normalizeStatusLabel(project.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('capitalize', priorityBadgeClassName(project.priority))}>
                          {project.priority || 'medium'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <CompactTableDate value={project.scheduled_start ?? project.start_date} />
                          <CompactTableDate value={project.scheduled_end ?? project.end_date} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(project.budget ?? 0)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" asChild>
                          <Link to={withServiceInstanceId(`/projects/${project.id}`, serviceInstanceId)}>
                            Open
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Page {page} of {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || projectsQuery.isLoading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || projectsQuery.isLoading}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
